import { HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AtlasLanguageService } from '@galaxy/atlas/core';
import { map } from 'rxjs/operators';
import {
  CRMCustomObjectApiService,
  CreateCrmObjectRequestInterface,
  CreateCrmObjectResponseInterface,
  DeleteCrmObjectRequestInterface,
  GetMultiCrmObjectRequestInterface,
  GetMultiCrmObjectResponseInterface,
  ListCrmObjectsRequestInterface,
  ListCrmObjectsResponseInterface,
  ListFieldOptionsRequestInterface,
  ListFiltersRequestInterface,
  ListFiltersResponseInterface,
  UpdateCrmObjectRequestInterface,
  ValidateCrmObjectRequestInterface,
  ValidateCrmObjectResponseInterface,
  BulkDeleteCrmObjectsRequestInterface,
} from '@vendasta/crm';
import { Observable } from 'rxjs';
import { AbstractCrmObjectService } from './_abstract.service';
import { listCustomObjects$, getMultiCustomObject$ } from '@vendasta/protos/crm/v1/crmcustom-object-service';
import {
  toLegacyGetMultiResponse,
  toLegacyListResponse,
  toProtoGetMultiRequest,
  toProtoListRequest,
} from '@galaxy/crm/services/adapter-service';

@Injectable({ providedIn: 'root' })
export class CustomObjectService implements AbstractCrmObjectService {
  private readonly languageService = inject(AtlasLanguageService);
  private locale = toSignal(this.languageService.language$);

  constructor(private readonly service: CRMCustomObjectApiService) {}

  createCrmObject(request: CreateCrmObjectRequestInterface): Observable<CreateCrmObjectResponseInterface> {
    return this.service.createCustomObject(request);
  }

  deleteCrmObject(request: DeleteCrmObjectRequestInterface): Observable<HttpResponse<null>> {
    return this.service.deleteCustomObject(request);
  }

  validateCrmObject(request: ValidateCrmObjectRequestInterface): Observable<ValidateCrmObjectResponseInterface> {
    return this.service.validateCustomObject(request);
  }

  updateCrmObject(request: UpdateCrmObjectRequestInterface): Observable<HttpResponse<null>> {
    return this.service.updateCustomObject(request);
  }

  listObjects(request: ListCrmObjectsRequestInterface): Observable<ListCrmObjectsResponseInterface> {
    return listCustomObjects$(toProtoListRequest(request)).pipe(map(toLegacyListResponse));
  }

  listObjectsFilters(request: ListFiltersRequestInterface): Observable<ListFiltersResponseInterface> {
    return this.service.listCustomObjectFilters({ ...request, locale: this.locale() });
  }

  getMultiObject(request: GetMultiCrmObjectRequestInterface): Observable<GetMultiCrmObjectResponseInterface> {
    return getMultiCustomObject$(toProtoGetMultiRequest(request)).pipe(map(toLegacyGetMultiResponse));
  }

  listFieldOptions(request: ListFieldOptionsRequestInterface): Observable<string[]> {
    return this.service.listCustomObjectFieldOptions(request).pipe(map((response) => response?.options || []));
  }

  bulkDelete(request: BulkDeleteCrmObjectsRequestInterface): Observable<HttpResponse<null>> {
    return this.service.bulkDeleteCustomObjects(request);
  }
}
